import { useMemo, useState, useCallback } from 'react';
import { Flex, TextField, Text, ScrollArea, IconButton, Badge, Separator, Button } from '@radix-ui/themes';
import { MagnifyingGlassIcon, Cross2Icon } from '@radix-ui/react-icons';
import { Collapsible, CollapsibleTrigger } from '@/components/ui/collapsible';
import { SidebarIcon } from '@/v2/assets/icons/SidebarIcon';
import cx from 'classnames';
import styles from './BriefTableOfContents.module.scss';

interface Heading {
  id: string;
  text: string;
  level: number;
  sectionContent?: string;
}

export interface SearchResult {
  headingId: string;
  headingText: string;
  snippet: string;
  matchIndex: number;
  matchText: string;
  lineNumber: number;
}

interface BriefTableOfContentsProps {
  content: string;
  onHeadingClick: (id: string) => void;
  onScrollToText?: (searchText: string, result?: SearchResult) => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  activeHeadingId?: string | null;
}

/**
 * Clean markdown formatting from text
 */
function cleanMarkdown(text: string): string {
  return text
    // Remove bold (**text** or __text__)
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/__(.+?)__/g, '$1')
    // Remove italic (*text* or _text_)
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/_(.+?)_/g, '$1')
    // Remove inline code (`text`)
    .replace(/`(.+?)`/g, '$1')
    // Remove strikethrough (~~text~~)
    .replace(/~~(.+?)~~/g, '$1')
    // Remove links [text](url)
    .replace(/\[(.+?)\]\(.+?\)/g, '$1')
    // Remove images ![alt](url)
    .replace(/!\[.*?\]\(.+?\)/g, '')
    .trim();
}

/**
 * Generate a slug from text for use as an anchor ID
 * Must match the logic in HeadingWithAnchors extension
 */
function generateSlug(text: string): string {
  const cleanedText = cleanMarkdown(text);

  return cleanedText
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Create a snippet with context around the search match
 */
function createSnippet(text: string, matchIndex: number, query: string, contextLength = 60): string {
  const start = Math.max(0, matchIndex - contextLength);
  const end = Math.min(text.length, matchIndex + query.length + contextLength);

  let snippet = text.slice(start, end);

  // Add ellipsis if truncated
  if (start > 0) snippet = '...' + snippet;
  if (end < text.length) snippet = snippet + '...';

  return snippet;
}

/**
 * Search for query in all content including text without headings
 */
function searchInContent(content: string, headings: Heading[], query: string): SearchResult[] {
  if (!query.trim()) return [];

  const results: SearchResult[] = [];
  const lowerQuery = query.toLowerCase();
  const contentLower = content.toLowerCase();

  // First, create a map of line numbers to headings
  const lines = content.split('\n');
  const lineToHeading: Map<number, Heading> = new Map();

  let currentHeadingForLine: Heading | null = null;
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const match = line.match(/^(#{1,3})\s+(.+)$/);

    if (match) {
      // Found a heading - find it in our headings array
      const rawText = match[2].trim();
      const id = generateSlug(rawText);
      const foundHeading = headings.find(h => h.id === id);
      if (foundHeading) {
        currentHeadingForLine = foundHeading;
      }
    }

    if (currentHeadingForLine) {
      lineToHeading.set(i, currentHeadingForLine);
    }
  }

  // Now search through the entire content
  let searchStart = 0;
  let matchIndex = contentLower.indexOf(lowerQuery, searchStart);
  let totalMatches = 0;
  const maxResults = 50; // Limit total results

  while (matchIndex !== -1 && totalMatches < maxResults) {
    // Find which line this match is on
    let charCount = 0;
    let lineNumber = 0;
    for (let i = 0; i < lines.length; i++) {
      if (charCount + lines[i].length >= matchIndex) {
        lineNumber = i + 1; // +1 to make it 1-indexed (line 1, line 2, etc.)
        break;
      }
      charCount += lines[i].length + 1; // +1 for newline
    }

    // Get the heading for this line (or null if before first heading)
    const heading = lineToHeading.get(lineNumber - 1); // -1 because map uses 0-indexed

    // Create snippet from the actual content
    const snippet = createSnippet(content, matchIndex, query);

    // Extract the actual matched text with some context for finding it
    const matchText = content.slice(matchIndex, matchIndex + lowerQuery.length);

    results.push({
      headingId: heading?.id || '',
      headingText: heading?.text || 'Document Start',
      snippet: cleanMarkdown(snippet),
      matchIndex,
      matchText,
      lineNumber,
    });

    totalMatches++;
    searchStart = matchIndex + lowerQuery.length;
    matchIndex = contentLower.indexOf(lowerQuery, searchStart);
  }

  return results;
}

/**
 * Extract headings with their section content from markdown
 */
function extractHeadings(content: string): Heading[] {
  const headings: Heading[] = [];
  const lines = content.split('\n');
  const seenIds = new Set<string>();

  let currentHeading: Heading | null = null;
  let currentContent: string[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const match = line.match(/^(#{1,3})\s+(.+)$/);

    if (match) {
      // Save previous heading with its content (only if not already seen)
      if (currentHeading && !seenIds.has(currentHeading.id)) {
        currentHeading.sectionContent = currentContent.join('\n').trim();
        headings.push(currentHeading);
        seenIds.add(currentHeading.id);
      }

      // Start new heading
      const level = match[1].length;
      const rawText = match[2].trim();
      const cleanedText = cleanMarkdown(rawText);
      const id = generateSlug(rawText);

      if (cleanedText && id && !seenIds.has(id)) {
        currentHeading = { id, text: cleanedText, level };
        currentContent = [];
      } else {
        // Skip duplicate heading or invalid heading
        currentHeading = null;
        currentContent = [];
      }
    } else if (currentHeading) {
      // Accumulate content for current heading
      currentContent.push(line);
    }
  }

  // Don't forget the last heading (only if not already seen)
  if (currentHeading && !seenIds.has(currentHeading.id)) {
    currentHeading.sectionContent = currentContent.join('\n').trim();
    headings.push(currentHeading);
  }

  return headings;
}

export function BriefTableOfContents({ content, onHeadingClick, onScrollToText, isCollapsed, onToggleCollapse, activeHeadingId }: BriefTableOfContentsProps) {
  const [searchQuery, setSearchQuery] = useState('');

  const headings = useMemo(() => extractHeadings(content), [content]);

  const searchResults = useMemo(() => {
    return searchInContent(content, headings, searchQuery);
  }, [content, headings, searchQuery]);

  const isSearching = searchQuery.trim().length > 0;

  const handleClearSearch = useCallback(() => {
    setSearchQuery('');
  }, []);

  const handleHeadingClick = useCallback((id: string) => {
    if (id) {
      onHeadingClick(id);
    }
  }, [onHeadingClick]);

  const handleSearchResultClick = useCallback((result: SearchResult) => {
    // Always scroll to the actual text match when searching
    if (onScrollToText) {
      onScrollToText(result.matchText, result);
    } else if (result.headingId) {
      onHeadingClick(result.headingId);
    } else {
      // Fallback: scroll to top
      const editorElement = document.querySelector('.tiptap');
      if (editorElement) {
        editorElement.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  }, [onHeadingClick, onScrollToText]);

  const highlightMatch = useCallback((text: string, query: string) => {
    if (!query) return text;

    const index = text.toLowerCase().indexOf(query.toLowerCase());
    if (index === -1) return text;

    return (
      <>
        {text.slice(0, index)}
        <span className={styles.searchHighlight}>
          {text.slice(index, index + query.length)}
        </span>
        {text.slice(index + query.length)}
      </>
    );
  }, []);

  if (isCollapsed) {
    return (
      <div className={styles.tocContainerCollapsed}>
        <Collapsible open={!isCollapsed} onOpenChange={(open) => open && onToggleCollapse()}>
          <CollapsibleTrigger asChild>
            <Button
              variant="ghost"
              size="2"
              aria-label="Expand table of contents"
              aria-pressed={!isCollapsed}
              className={styles.toggleButton}
            >
              <SidebarIcon size={14} />
            </Button>
          </CollapsibleTrigger>
        </Collapsible>
      </div>
    );
  }

  if (headings.length === 0) {
    return (
      <div className={styles.tocContainer}>
        <Flex direction="column" gap="2">
          <Flex justify="between" align="center">
            <Text size="2" weight="medium" color="gray">
              Table of Contents
            </Text>
            <Collapsible open={!isCollapsed} onOpenChange={(open) => !open && onToggleCollapse()}>
              <CollapsibleTrigger asChild>
                <Button
                  variant="ghost"
                  size="2"
                  aria-label="Collapse table of contents"
                  aria-pressed={!isCollapsed}
                >
                  <SidebarIcon size={14} />
                </Button>
              </CollapsibleTrigger>
            </Collapsible>
          </Flex>
          <Text size="1" color="gray">
            Add headings to your brief to see the outline here
          </Text>
        </Flex>
      </div>
    );
  }

  return (
    <div className={styles.tocContainer}>
      <Flex direction="column" gap="2" style={{ height: '100%' }}>
        <Flex justify="between" align="center">
          <Flex align="center" gap="2">
            <Text size="2" weight="medium" color="gray">
              {isSearching ? 'Search Results' : 'Table of Contents'}
            </Text>
            {isSearching && searchResults.length > 0 && (
              <Badge size="1" variant="soft" color="blue">
                {searchResults.length}
              </Badge>
            )}
          </Flex>
          <Collapsible open={!isCollapsed} onOpenChange={(open) => !open && onToggleCollapse()}>
            <CollapsibleTrigger asChild>
              <Button
                variant="ghost"
                size="2"
                aria-label="Collapse table of contents"
                aria-pressed={!isCollapsed}
              >
                <SidebarIcon size={14} />
              </Button>
            </CollapsibleTrigger>
          </Collapsible>
        </Flex>

        <TextField.Root
          placeholder="Search in document..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="1"
        >
          <TextField.Slot>
            <MagnifyingGlassIcon height="14" width="14" />
          </TextField.Slot>
          {searchQuery && (
            <TextField.Slot>
              <IconButton
                size="1"
                variant="ghost"
                onClick={handleClearSearch}
                aria-label="Clear search"
              >
                <Cross2Icon height="14" width="14" />
              </IconButton>
            </TextField.Slot>
          )}
        </TextField.Root>

        <ScrollArea type="auto" scrollbars="vertical" style={{ flex: 1 }}>
          {isSearching ? (
            // Search results view
            <Flex direction="column" gap="2">
              {searchResults.length === 0 ? (
                <Text size="1" color="gray" style={{ padding: '8px 0' }}>
                  No results found for "{searchQuery}"
                </Text>
              ) : (
                searchResults.map((result, index) => (
                  <div key={`${result.headingId || 'doc-start'}-${index}`}>
                    <button
                      className={styles.searchResultItem}
                      onClick={() => handleSearchResultClick(result)}
                    >
                      <Flex align="center" gap="2">
                        <Text size="1" weight="medium" className={styles.searchResultHeading}>
                          {result.headingText}
                        </Text>
                        {!result.headingId && (
                          <Badge size="1" variant="soft" color="gray">
                            top
                          </Badge>
                        )}
                      </Flex>
                      <Text size="1" color="gray" className={styles.searchResultSnippet}>
                        {highlightMatch(result.snippet, searchQuery)}
                      </Text>
                    </button>
                    {index < searchResults.length - 1 && (
                      <Separator size="4" my="2" />
                    )}
                  </div>
                ))
              )}
            </Flex>
          ) : (
            // Table of contents view
            <Flex direction="column" gap="1">
              {headings.map((heading, index) => (
                <button
                  key={`${heading.id}-${index}`}
                  className={cx(styles.tocItem, { [styles.active]: activeHeadingId === heading.id })}
                  data-level={heading.level}
                  onClick={() => handleHeadingClick(heading.id)}
                  title={heading.text}
                >
                  <Text size="1" className={styles.tocItemText}>
                    {heading.text}
                  </Text>
                </button>
              ))}
            </Flex>
          )}
        </ScrollArea>
      </Flex>
    </div>
  );
}
